/**
 * Auth Failed State Management
 * 
 * Manages the terminal auth failure state using Capacitor Preferences
 * for persistence on iOS. Falls back to sessionStorage on web.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const AUTH_FAILED_KEY = 'auth_failed';

// In-memory cache for sync access
let authFailedCache: boolean = false;

/**
 * Initialize the auth failed state from storage.
 * Call this at app startup.
 */
export const initAuthFailedState = async (): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key: AUTH_FAILED_KEY });
    authFailedCache = value === 'true';
  } else {
    authFailedCache = sessionStorage.getItem(AUTH_FAILED_KEY) === 'true';
  }
  console.log('[Auth] Auth failed state initialized:', authFailedCache);
};

/**
 * Check if auth has permanently failed (sync).
 * Uses cached value for performance.
 */
export const hasAuthFailed = (): boolean => {
  return authFailedCache;
};

/**
 * Set auth as permanently failed.
 */
export const setAuthFailed = async (): Promise<void> => {
  authFailedCache = true;
  console.log('[Auth] Setting auth failed state');
  
  if (Capacitor.isNativePlatform()) {
    await Preferences.set({ key: AUTH_FAILED_KEY, value: 'true' });
  } else {
    sessionStorage.setItem(AUTH_FAILED_KEY, 'true');
  }
};

/**
 * Clear auth failed state (on successful login).
 */
export const clearAuthFailed = async (): Promise<void> => {
  authFailedCache = false;
  console.log('[Auth] Clearing auth failed state');
  
  if (Capacitor.isNativePlatform()) {
    await Preferences.remove({ key: AUTH_FAILED_KEY });
  } else {
    sessionStorage.removeItem(AUTH_FAILED_KEY);
  }
};

